/*
 * Right sidebar, narrow, stuck to the top even when rest of the page scrolls.
 */

import dynamic from 'next/dynamic'
import React, { ReactNode } from 'react'
import styled from 'styled-components'

import { useScroll } from '@hozana/hooks/useScroll'
import type { TBreakpoint, TResponsiveProps } from '@hozana/styling/responsive'

import { Div, TDivProps } from 'elements/layout/Div'

const StickyBox = dynamic(() => import('react-sticky-box'), { loading: () => null })

const responsiveProps = { w: 'width' } as const

const StyledDiv = styled.div`
  height: 100%;
  ${__SERVER__ &&
  `
    & > div {
      height: 100%;
    }
  `}
`

type TStyledAsideProps = {
  offsetTop: number
} & TDivProps

const Aside = styled(Div).attrs<TStyledAsideProps, Partial<TStyledAsideProps>>({ as: 'aside' })`
  ${({ offsetTop }) =>
    __SERVER__ &&
    `
    position: sticky;
    top: ${offsetTop}px;
  `}
`

type TSidebarProps = {
  visible?: TBreakpoint[]
  offsetTop?: number
  offsetBottom?: number
  children: ReactNode | ((p: { distanceFromTop: number }) => ReactNode)
} & TResponsiveProps<typeof responsiveProps>

export const Sidebar: TFCC<TSidebarProps> = ({
  visible = ['lg', 'xl'],
  children,
  w = '265px',
  offsetTop = 55,
  offsetBottom = 70,
}) => {
  const { pageYOffset } = useScroll()

  return (
    <StyledDiv>
      <StickyBox offsetTop={offsetTop} offsetBottom={offsetBottom}>
        <Aside offsetTop={offsetTop} visible={visible} w={w}>
          {typeof children === 'function' ? children({ distanceFromTop: pageYOffset }) : children}
        </Aside>
      </StickyBox>
    </StyledDiv>
  )
}
